import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Sparkles, Zap, Building2, Crown, ArrowRight } from "lucide-react";

const PLAN_ICONS = {
  starter: Sparkles,
  pro: Zap,
  studio: Building2,
  business: Crown,
};

const PLAN_ACCENT = {
  starter: "#8C8B8A",
  pro: "#2C4C3B",
  studio: "#C85A32",
  business: "#1C1B1A",
};

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null); // lookup_key currently in checkout
  const [trialDays, setTrialDays] = useState(7);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/subscription/plans")
      .then(({ data }) => {
        setPlans(data.plans || []);
        setTrialDays(data.trial_days || 7);
      })
      .catch((e) => toast.error("Impossibile caricare i piani: " + (e?.message || "")));
  }, []);

  const startCheckout = async (lookup_key) => {
    const token = localStorage.getItem("segretaria_token");
    if (!token) {
      // Send them to login with returnTo param
      navigate(`/login?returnTo=/pricing&plan=${lookup_key}`);
      return;
    }
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      setBusy(null);
      toast.error(err?.response?.data?.detail || "Errore avvio checkout");
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#1C1B1A]">
      {/* Header */}
      <header className="border-b border-[#E5E4E0] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#2C4C3B] flex items-center justify-center">
              <span className="text-white font-mono text-xs font-bold">AI</span>
            </div>
            <div>
              <div className="font-display text-lg leading-tight">Segretaria AI</div>
              <div className="label-mono text-[9px]">AB Home Venezia</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-mono uppercase tracking-[0.15em] text-[#5C5B5A] hover:text-[#1C1B1A]">
              Accedi
            </Link>
            <Link to="/register">
              <Button
                data-testid="pricing-register-cta"
                className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-mono uppercase text-xs tracking-[0.2em]"
              >
                Prova gratis
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 py-16 text-center">
        <div className="label-mono mb-4">Piani &amp; Prezzi</div>
        <h1 className="font-display text-5xl sm:text-6xl font-light leading-tight max-w-3xl mx-auto">
          Rispondi alle email dei clienti mentre dormi
        </h1>
        <p className="mt-6 text-base text-[#5C5B5A] max-w-2xl mx-auto leading-relaxed">
          La tua segretaria virtuale gestisce le richieste con AI Claude Sonnet 4.5, individua l&apos;immobile giusto e
          risponde in italiano, inglese, spagnolo, francese e tedesco. Provala {trialDays} giorni gratis.
        </p>
        <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 bg-[#FDF1EB] border border-[#C85A32]">
          <Sparkles size={14} className="text-[#C85A32]" />
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#C85A32]">
            {trialDays} giorni gratis · nessun addebito prima della fine del trial
          </span>
        </div>
      </section>

      {/* Plans grid */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((p) => {
            const Icon = PLAN_ICONS[p.id] || Sparkles;
            const isPopular = p.id === "pro";
            const accent = PLAN_ACCENT[p.id];
            return (
              <div
                key={p.lookup_key}
                data-testid={`plan-card-${p.id}`}
                className={`relative bg-white border p-6 flex flex-col ${
                  isPopular ? "border-[#2C4C3B] border-2 shadow-lg" : "border-[#E5E4E0]"
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#2C4C3B] text-white text-[10px] font-mono uppercase tracking-[0.2em] px-3 py-1">
                    Più popolare
                  </div>
                )}
                <Icon size={24} style={{ color: accent }} />
                <h3 className="font-display text-3xl mt-4 font-light">{p.name}</h3>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="font-display text-5xl font-light">€{p.price_eur}</span>
                  <span className="text-sm text-[#8C8B8A]">/mese</span>
                </div>
                <div className="mt-3 text-xs font-mono uppercase tracking-[0.15em] text-[#5C5B5A]">
                  {p.email_quota.toLocaleString("it-IT")} risposte AI/mese
                </div>

                <ul className="mt-6 space-y-2.5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#1C1B1A]">
                      <Check size={16} className="text-[#2C4C3B] mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  data-testid={`plan-cta-${p.id}`}
                  onClick={() => startCheckout(p.lookup_key)}
                  disabled={busy === p.lookup_key}
                  className={`mt-6 w-full font-mono uppercase text-xs tracking-[0.2em] ${
                    isPopular
                      ? "bg-[#2C4C3B] hover:bg-[#1E3629] text-white"
                      : "bg-[#1C1B1A] hover:bg-[#000] text-white"
                  }`}
                >
                  {busy === p.lookup_key ? "Attendere…" : `Prova ${trialDays} giorni gratis`}
                  <ArrowRight size={14} className="ml-2" />
                </Button>
              </div>
            );
          })}
        </div>

        {/* Enterprise contact card */}
        <div className="mt-8 bg-[#1C1B1A] text-white p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="label-mono text-[#C85A32]">Enterprise</div>
            <h3 className="font-display text-3xl font-light mt-1">Franchising · Multi-sede · Volumi custom</h3>
            <p className="text-sm text-[#8C8B8A] mt-2 max-w-xl">
              Prezzi personalizzati per reti di agenzie o volumi &gt; 4.000 email/mese. Onboarding dedicato + SLA.
            </p>
          </div>
          <a href="mailto:info@abhomevenezia.com?subject=Segretaria AI - Enterprise">
            <Button className="bg-white text-[#1C1B1A] hover:bg-[#F7F5F0] font-mono uppercase text-xs tracking-[0.2em]">
              Contattaci
            </Button>
          </a>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-16 border-t border-[#E5E4E0]">
        <div className="label-mono text-center mb-4">Domande frequenti</div>
        <h2 className="font-display text-4xl font-light text-center mb-10">Prima che tu chieda…</h2>
        <div className="space-y-6">
          {[
            {
              q: "Come funziona la prova gratuita di 7 giorni?",
              a: "Ti chiediamo la carta di credito per attivare, ma non facciamo alcun addebito nei primi 7 giorni. Se cancelli prima della scadenza non paghi nulla. Alla fine del trial l'abbonamento parte automaticamente.",
            },
            {
              q: "Posso cambiare piano in qualsiasi momento?",
              a: "Sì. Dal portale clienti (accessibile dopo l'iscrizione) puoi fare upgrade, downgrade o cancellare. Le differenze di prezzo vengono calcolate pro-rata.",
            },
            {
              q: "Cosa succede se supero la quota mensile di email?",
              a: "L'AI smette di rispondere fino al ciclo successivo e ricevi un avviso per l'upgrade. Non ci sono costi a sorpresa: paghi solo il piano scelto.",
            },
            {
              q: "Quali provider email supportate?",
              a: "Gmail, Outlook/Hotmail/outlook.it (via OAuth2 nativo Microsoft), e qualsiasi IMAP/SMTP standard (Aruba, Register, Libero, provider personalizzati).",
            },
            {
              q: "I miei dati sono al sicuro?",
              a: "Sì. Password e token OAuth cifrati (Fernet). 2FA disponibile (Google Authenticator/Authy). Hosting in Europa (GDPR).",
            },
          ].map((it) => (
            <div key={it.q} className="border-b border-[#E5E4E0] pb-6">
              <h4 className="font-display text-xl mb-2">{it.q}</h4>
              <p className="text-sm text-[#5C5B5A] leading-relaxed">{it.a}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-[#E5E4E0] py-8 text-center text-xs font-mono uppercase tracking-[0.2em] text-[#8C8B8A]">
        © 2026 AB Home Venezia · Segretaria AI
      </footer>
    </div>
  );
}
